import { useState } from 'react';
import * as R from 'ramda';

import NavigationButton from '@atom/components/common/map/mapDrawerContent/NavigationButton';
import NavigationControls from '@atom/components/common/map/mapDrawerContent/NavigationControls';
import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import WorkPriorityIcon from '@atom/components/common/WorkPriorityIcon';
import { Icon, IconButton, Progress } from '@atom/mui';
import { WorkOrderDetailType } from '@atom/types/work';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import { getStaticImage } from '@atom/utilities/mapUtilities';
import userUtilities from '@atom/utilities/userUtilities';

import WorkSummaryInfo from './WorkSummaryInfo';
import WorkSummaryTasks from './WorkSummaryTasks';
import WorkSummaryTeam from './WorkSummaryTeam';

import classes from './workSummaryTray.module.css';

const views = [
  {
    value: 'info',
    icon: 'info',
  },
  {
    value: 'tasks',
    icon: 'playlist_add_check',
  },
  {
    value: 'team',
    icon: 'supervisor_account',
  },
];

interface Props {
  workOrderDetail?: WorkOrderDetailType;
  loading?: boolean;
  onCancel?: () => void;
}

const WorkSummaryTray = ({ workOrderDetail, loading, onCancel }: Props) => {
  const [activeView, setActiveView] = useState<string>('info');

  const getTrayContentBody = () => {
    switch (activeView) {
      case 'info': {
        return <WorkSummaryInfo work={workOrderDetail} />;
      }
      case 'tasks': {
        return <WorkSummaryTasks tasks={workOrderDetail?.tasks} />;
      }
      case 'team': {
        return (
          <WorkSummaryTeam
            users={userUtilities.rollUpTaskUsers(workOrderDetail?.tasks)}
          />
        );
      }
      default: {
        return <WorkSummaryInfo work={workOrderDetail} />;
      }
    }
  };

  const getSummaryImage = () => {
    if (R.isNil(workOrderDetail)) {
      return getStaticImage(null);
    }

    return !workOrderDetail.mainPhotoFileId
      ? getStaticImage(workOrderDetail.location)
      : `${FILES_ENDPOINT}/${workOrderDetail.mainPhotoFileId}_180`;
  };

  const body = getTrayContentBody();

  const imageUrl = {
    backgroundImage: `url('${getSummaryImage()}')`,
  };

  return (
    <div className={classes.container}>
      {loading || !workOrderDetail ? (
        <div className={classes.spinnerContainer}>
          <Progress />
        </div>
      ) : (
        <div className={classes.trayContainer}>
          <div className={classes.headerContainer}>
            <div className={classes.headerTitleContainer}>
              <div className={classes.headerTitle}>{workOrderDetail.name}</div>
              <div className={classes.exitContainer}>
                <IconButton onClick={onCancel}>
                  <Icon>close</Icon>
                </IconButton>
              </div>
            </div>
            <div className={classes.priorityStatusContainer}>
              <div className={classes.priorityContainer}>
                <WorkPriorityIcon priorityId={workOrderDetail.priorityId} />
              </div>
              <WorkOrderStatusPill
                statusId={workOrderDetail.statusId}
                className="dashboard-map-list"
              />
            </div>
          </div>
          <div className={classes.trayContentContainer}>
            <div className={classes.summaryImageContainer} style={imageUrl} />
            <NavigationControls
              views={views}
              activeView={activeView}
              setActiveView={setActiveView}
            />
            <div>{body}</div>
          </div>
          <NavigationButton
            label="View Full Detail"
            navigateUrl={`/workOrders/${workOrderDetail?.id}`}
          />
        </div>
      )}
    </div>
  );
};

export default WorkSummaryTray;
